import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FolderOpen,
  Loader2,
  FileText,
  Image as ImageIcon,
  Video,
  Eye,
  Download,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  usePatientDocuments,
  useGetDocumentDownloadUrl,
  type DocumentRow,
} from '@/hooks/useDocuments';
import {
  FilePreviewOverlay,
  type FilePreviewSource,
} from '@/components/documents/FilePreviewOverlay';

interface PatientDocumentsSectionProps {
  clinicId: string;
  patientId: string;
  isAdmin?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mimeType: string) {
  if (mimeType.startsWith('video/')) return Video;
  if (mimeType.startsWith('image/')) return ImageIcon;
  return FileText;
}

export function PatientDocumentsSection({
  clinicId,
  patientId,
  isAdmin: _isAdmin = false,
}: PatientDocumentsSectionProps) {
  const { t } = useTranslation();
  const docs = usePatientDocuments(clinicId, patientId);
  const getDownloadUrl = useGetDocumentDownloadUrl();
  const [preview, setPreview] = useState<FilePreviewSource | null>(null);

  if (!clinicId) return null;

  const handleView = (doc: DocumentRow) => {
    setPreview({
      type: 'document',
      documentId: doc.id,
      filename: doc.original_filename,
      mimeType: doc.mime_type,
    });
  };

  const handleDownload = async (doc: DocumentRow) => {
    try {
      const url = await getDownloadUrl.mutateAsync(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(t('documents.download_failed'));
    }
  };

  if (docs.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-card-dark">
      {preview && (
        <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} />
      )}

      <div className="mb-4 flex items-center gap-2">
        <FolderOpen className="h-5 w-5 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('patients.tabs.documents')}
        </h2>
      </div>

      {docs.data?.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('patients.files.empty')}
        </p>
      ) : (
        <ul className="space-y-2">
          {docs.data?.map((doc) => {
            const Icon = fileIcon(doc.mime_type);
            return (
              <li
                key={doc.id}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60"
              >
                <Icon className="h-4 w-4 shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => handleView(doc)}
                    className="block w-full truncate text-left text-sm text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
                  >
                    {doc.original_filename}
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(doc.size_bytes)}
                    {' · '}
                    {new Date(doc.created_at).toLocaleDateString()}
                    {doc.tags.length > 0 && ` · ${doc.tags.join(', ')}`}
                  </p>
                </div>
                <button
                  type="button"
                  title={t('documents.view')}
                  onClick={() => handleView(doc)}
                  className="rounded p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  title={t('documents.download')}
                  onClick={() => void handleDownload(doc)}
                  disabled={getDownloadUrl.isPending}
                  className="rounded p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  <Download className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
